// wayai eval capture <conversation_id> — POST a real conversation to /api/evals/scenarios/from-conversation and write the result as eval YAML.
import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { requireAuth } from '../lib/auth.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { ApiClient } from '../lib/api-client.js';
import { findLocalHubFolder, resolveActiveHubId } from '../lib/workspace.js';
import { slugify, UUID_RE } from '../lib/utils.js';
import { YAML_DUMP_OPTIONS, buildEvalYamlObject } from '../lib/yaml-writer.js';
import type { HubAsCodeEval } from '../lib/types.js';

interface ParsedArgs {
  conversationId: string;
  setName: string | null;
  evalName: string | null;
  evaluatorInstructions: string | null;
}

// Scenario sets are single folder names — reject separators and traversal so they fit the parser's flat-set model.
function isValidSetName(name: string): boolean {
  if (name.length === 0 || name === '.' || name === '..') return false;
  if (name.startsWith('.')) return false;
  return !/[\/\\\0]/.test(name);
}

function parseArgs(args: string[]): ParsedArgs {
  if (args.length === 0 || args[0].startsWith('-')) {
    console.error('Usage: wayai eval capture <conversation_id> [--set <name>] [--name <eval_name>] [--instructions <text>]');
    process.exit(1);
  }

  const conversationId = args[0];
  if (!UUID_RE.test(conversationId)) {
    console.error(`Invalid conversation ID "${conversationId}". Expected a UUID.`);
    process.exit(1);
  }

  let setName: string | null = null;
  let evalName: string | null = null;
  let evaluatorInstructions: string | null = null;

  for (let i = 1; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--set') {
      setName = args[++i] ?? '';
      if (!setName) { console.error('--set requires a value.'); process.exit(1); }
      if (!isValidSetName(setName)) {
        console.error(`Invalid --set value "${setName}". Scenario set names must be a single folder name (no slashes, no '.', no '..', no leading dot).`);
        process.exit(1);
      }
    } else if (arg === '--name') {
      evalName = args[++i] ?? '';
      if (!evalName) { console.error('--name requires a value.'); process.exit(1); }
    } else if (arg === '--instructions') {
      evaluatorInstructions = args[++i] ?? '';
      if (!evaluatorInstructions) { console.error('--instructions requires a value.'); process.exit(1); }
    } else {
      console.error(`Unknown argument: ${arg}`);
      process.exit(1);
    }
  }

  return { conversationId, setName, evalName, evaluatorInstructions };
}

export async function evalCaptureCommand(args: string[]): Promise<void> {
  // Resolve hub before parseArgs in case parseArgs rejects unknown flags
  const hubId = resolveActiveHubId(args);
  const parsed = parseArgs(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  const hubFolder = findLocalHubFolder(hubId);
  if (!hubFolder) {
    console.error('Could not find a local hub folder for this hub. Run `wayai pull` first.');
    process.exit(1);
  }

  // Fall back to 'Default' on both sides so the local path and platform set agree.
  const targetSetName = parsed.setName ?? 'Default';
  const setFolderName = targetSetName;
  const scenarioName = parsed.evalName ?? `Capture ${parsed.conversationId.slice(0, 8)}`;
  const slug = slugify(scenarioName);
  const evalsDir = path.join(hubFolder, 'evals');
  const targetDir = path.join(evalsDir, setFolderName);
  const targetPath = path.join(targetDir, `${slug}.yaml`);
  // Defense-in-depth: refuse paths escaping evals/, mirroring writeEvalYamlFiles.
  if (!targetPath.startsWith(evalsDir + path.sep)) {
    console.error(`Resolved path "${path.relative(hubFolder, targetPath)}" escapes evals/. Aborting.`);
    process.exit(1);
  }
  if (fs.existsSync(targetPath)) {
    console.error(`File already exists: ${path.relative(hubFolder, targetPath)}. Use --name to choose a different name.`);
    process.exit(1);
  }

  // 2. Resolve scenario_set_id (find or create by name).
  console.log('Resolving scenario set...');
  const sets = await client.listScenarioSets(hubId);
  let scenarioSetId = sets.data.scenario_sets.find((s) => s.set_name === targetSetName)?.scenario_set_id;

  if (!scenarioSetId) {
    console.log(`Creating scenario set "${targetSetName}"...`);
    const created = await client.createScenarioSet(hubId, targetSetName);
    scenarioSetId = created.data.scenario_set.scenario_set_id;
  }

  // 3. Capture from conversation.
  console.log(`Capturing conversation ${parsed.conversationId}...`);
  const result = await client.createScenarioFromConversation({
    hub_id: hubId,
    conversation_id: parsed.conversationId,
    scenario_set_id: scenarioSetId,
    scenario_name: scenarioName,
    ...(parsed.evaluatorInstructions ? { evaluator_instructions: parsed.evaluatorInstructions } : {}),
  });

  const captured = result.data.eval;

  // 4. Build the on-disk YAML via the shared eval-writer logic so capture and
  // pull produce identical shapes. The display name is initially the agent_id
  // — `wayai pull` resolves it to the proper name on next sync.
  const evalEntry: HubAsCodeEval = {
    id: captured.eval_id,
    name: scenarioName,
    agent: captured.responder_agent_id,
    agent_id: captured.responder_agent_id,
    path: setFolderName,
    history: captured.message_history,
    input: captured.message_text,
    expected: captured.message_expected_response,
    ...(captured.evaluator_instructions ? { evaluator_instructions: captured.evaluator_instructions } : {}),
  };
  const yamlObj = buildEvalYamlObject(evalEntry, slug);

  fs.mkdirSync(targetDir, { recursive: true });
  fs.writeFileSync(targetPath, yaml.dump(yamlObj, YAML_DUMP_OPTIONS), 'utf-8');

  const relPath = path.relative(process.cwd(), targetPath);
  console.log(`\nWrote ${relPath}`);
  console.log('Run `wayai pull` to refresh the agent display name, then commit. The scenario is already on the platform.');
}
